import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AddOutlined,
  EditOutlined,
  PeopleOutlined,
  PetsOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasAnyPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { customersService } from "../../services";
import CustomerPetsDialog from "./CustomerPetsDialog";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const isInternalEmail = (value) => String(value || "").toLowerCase().endsWith("@clientes.local");

function CustomersListPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewPets = hasAnyPermission(
    effectivePerms,
    ["grooming_view", "grooming_manage", "grooming_convert_sale"],
    isSuperuser
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("true");
  const [petsDialogCustomer, setPetsDialogCustomer] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!location.state?.message) return;
    showSnack(location.state.message, location.state.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, showSnack]);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        active: activeFilter === "" ? undefined : activeFilter,
      };
      const data = await customersService.list(params);
      setRows(toArray(data));
    } catch {
      setRows([]);
      showSnack("No se pudo cargar el listado de clientes.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, debouncedSearch, showSnack]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openPetsDialog = useCallback(
    (customer) => {
      if (!branchId) {
        showSnack("Selecciona una sucursal para gestionar mascotas de Grooming.", "warning");
        return;
      }
      setPetsDialogCustomer(customer);
    },
    [branchId, showSnack]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      {
        field: "name",
        headerName: "Cliente",
        minWidth: 240,
        flex: 1,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, py: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
              {row?.name || "Sin nombre"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
              {row?.address || "Sin direccion registrada"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "nit",
        headerName: "NIT",
        width: 150,
        renderCell: ({ value }) => (
          <Chip size="small" variant="outlined" label={value || "CF"} />
        ),
      },
      {
        field: "phone_number",
        headerName: "Telefono",
        width: 150,
        renderCell: ({ value }) => (
          <Typography sx={{ fontSize: 12.5 }}>{value || "-"}</Typography>
        ),
      },
      {
        field: "email",
        headerName: "Correo",
        minWidth: 220,
        flex: 1,
        renderCell: ({ value }) => {
          if (!value) {
            return <Chip size="small" variant="outlined" label="Sin correo" />;
          }

          if (isInternalEmail(value)) {
            return (
              <Tooltip title={value}>
                <Chip size="small" color="default" variant="outlined" label="Interno" />
              </Tooltip>
            );
          }

          return (
            <Typography sx={{ fontSize: 12.5 }} noWrap>
              {value}
            </Typography>
          );
        },
      },
      {
        field: "city",
        headerName: "Ciudad",
        width: 160,
        valueGetter: (_, row) => row?.city?.name || "-",
      },
      {
        field: "is_active",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activo" : "Inactivo"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        minWidth: canViewPets ? 240 : 130,
        flex: canViewPets ? 1 : undefined,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlined fontSize="small" />}
              onClick={() => navigate(`/customers/${row.id}/edit`)}
            >
              Editar
            </Button>
            {canViewPets ? (
              <Button
                size="small"
                variant="text"
                startIcon={<PetsOutlined fontSize="small" />}
                onClick={() => openPetsDialog(row)}
                disabled={!branchId}
              >
                Mascotas
              </Button>
            ) : null}
          </Stack>
        ),
      },
    ],
    [branchId, canViewPets, navigate, openPetsDialog]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText
          title="Clientes"
          subtitle="Listado de clientes registrados"
          icon={PeopleOutlined}
          width="60%"
        />

        <Stack direction="row" spacing={1}>
          <Tooltip title="Refrescar listado">
            <IconButton onClick={loadCustomers}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={() => navigate("/customers/create")}
          >
            Nuevo cliente
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            size="small"
            placeholder="Buscar por nombre, NIT, telefono o correo..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Activos</MenuItem>
              <MenuItem value="false">Inactivos</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Box sx={{ height: isMobile ? "calc(100vh - 255px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          hideFooterSelectedRowCount
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
          sx={{
            bgcolor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: `${theme.palette.primary.main} !important`,
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: `${theme.palette.primary.contrastText} !important`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        />
      </Box>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />

      <CustomerPetsDialog
        open={Boolean(petsDialogCustomer)}
        onClose={() => setPetsDialogCustomer(null)}
        customer={petsDialogCustomer}
      />
    </Box>
  );
}

export default CustomersListPage;
